import React, {
  CSSProperties, FC, ReactNode, useCallback, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import useIsomorphicLayoutEffect from 'src/common/hooks/useIsomorphicLayoutEffect';
import useOnclickOutside from 'react-cool-onclickoutside';
import styles from './Tooltip.module.scss';
import Icon from '../IconDS22';

const getScrollableParent = (node: HTMLElement) => {
  let style = getComputedStyle(node);
  const excludeStaticParent = style.position === 'absolute';
  const overflowRegex = /(auto|scroll)/;

  if (style.position === 'fixed') {
    return document.body;
  }
  // eslint-disable-next-line no-cond-assign
  for (let parent: HTMLElement | null = node; (parent = parent.parentElement);) {
    style = getComputedStyle(parent);
    if (excludeStaticParent && style.position === 'static') {
      // eslint-disable-next-line no-continue
      continue;
    }
    if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) {
      return parent;
    }
  }

  return document.body;
};

type TooltipDirection = 'bottom' | 'top' | 'right';

type TooltipCTAProps = {
  children?: ReactNode;
  className: string;
  // Linting is disabled for these props because eslint incorrectly reports them as unused
  // eslint-disable-next-line react/no-unused-prop-types
  isOpen: boolean; open: () => void; close: () => void;
  toggle: () => void;
  tooltipCTARef?: React.LegacyRef<HTMLButtonElement>;
};

export const DefaultCTA: FC<TooltipCTAProps> = ({
  children, className, toggle, tooltipCTARef,
}) => (
  <button
    type="button"
    className={className}
    onClick={toggle}
    ref={tooltipCTARef}
  >
    {children || (
      <Icon
        id="info"
        className={styles.icon}
      />
    )}
  </button>
);

export type TooltipProps = {
  children: React.ReactNode;
  renderCTA?: FC<TooltipCTAProps>;
  classNameModifier?: string;
  insertDangerously?: boolean;
  direction?: TooltipDirection;
};

// Distance from edge
const MARGIN = 8;
type Position = { x: number };

const Tooltip: FC<TooltipProps> = ({
  renderCTA: CTA = DefaultCTA,
  children,
  classNameModifier,
  insertDangerously = false,
  direction,
}) => {
  const tipRef = useRef<HTMLDivElement>(null);
  // offscreen
  const [position, setPosition] = useState<Position>({ x: -10000 });
  const [arrow, setArrow] = useState<Position>({ x: 0 });
  const [area, setArea] = useState<HTMLElement>();
  const [directionModifier, setDirectionModifier] = useState<TooltipDirection>(direction || 'top');
  const [scroll, setScroll] = useState(0);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState(false);

  const toggle = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const open = useCallback(() => {
    setOpen(true);
  }, []);

  useOnclickOutside(close, { refs: [tipRef, ctaRef] });

  useEffect(() => {
    if (!tipRef.current || !ctaRef.current || !open || !area || !window) {
      return;
    }
    const {
      x: containerX,
      y: containerY,
      width: containerWidth,
      height: containerHeight,
    } = area.getBoundingClientRect();
    const areaX = containerX + MARGIN;
    const areaY = containerY + MARGIN;
    const areaWidth = containerWidth - MARGIN * 2;
    const areaHeight = containerHeight - MARGIN * 2;
    const { width, height } = tipRef.current.getBoundingClientRect();
    const {
      x: ctaX, y: ctaY, width: ctaWidth, height: ctaHeight,
    } = ctaRef.current.getBoundingClientRect();

    const halfCta = ctaWidth / 2;
    let x = halfCta;
    const initial = x;

    // Tip is centred horizontally in CSS
    const halfWidth = width / 2;

    // edge of tip overflows left or right
    const tipLeft = x + ctaX - halfWidth;
    if (tipLeft < areaX) {
      x += areaX - tipLeft;
    }

    // Prioritize right to avoid creating scrollbars
    const tipRight = x + ctaX + halfWidth;
    if (tipRight > areaX + areaWidth) {
      x += (areaX + areaWidth) - tipRight;
    }

    const y = ctaY + ctaHeight;

    // Force override type as value does exist
    let rem = parseInt((window.getComputedStyle(document.body).getPropertyValue('font-size')), 10);
    if (!rem || Number.isNaN(rem)) {
      rem = 16;
    }
    const tipOffsetY = (2 * rem) / 2; // taken from &_bottom in CSS (spacing-08)

    // Prioritize moving up to avoid creating scrollbars
    if (y + height > areaY + areaHeight - tipOffsetY) {
      setDirectionModifier('top');
    } else {
      setDirectionModifier('bottom');
    }

    setPosition({ x });
    setArrow({ x: halfWidth + (initial - x) });

    if (direction === 'right') {
      // NOTE: Position values are temporarily hardcoded for admin pricing page.
      // TODO: Replace with dynamic position calculation.
      setPosition({ x: 242 });
      setArrow({ x: 0 });
      setDirectionModifier('right');
    }
  }, [isOpen, children, open, area, scroll, direction]);

  useIsomorphicLayoutEffect(
    () => {
      // the first two nodes are intentionally skipped, because the button and root
      // node are not the nodes we are looking for.
      const getTooltipsParent = (): HTMLElement => (
        ctaRef?.current?.parentNode as HTMLElement
      );
      const container = getScrollableParent(getTooltipsParent());
      const onChange = () => {
        setArea(container);
      };

      const onScroll = () => {
        // Only used to trigger re-layout, value not important, only changing value
        const scrollValue = container === document.body && window
          ? window.scrollY
          : container.scrollTop;
        setScroll(scrollValue ?? 0);
        // Update in case scroll changes y position
        onChange();
      };

      // Container might resize
      const resizeObserver = new ResizeObserver(() => {
        onChange();
      });
      // Container resize and window resize in one
      resizeObserver.observe(container);

      onChange();
      onScroll();

      const scrollTarget = container === document.body && window
        ? window
        : container;
      scrollTarget.addEventListener('scroll', onScroll);
      if (scrollTarget !== window) {
        window.addEventListener('scroll', onScroll);
      }

      return () => {
        scrollTarget.removeEventListener('scroll', onScroll);
        window.removeEventListener('scroll', onScroll);
        resizeObserver.disconnect();
        // release DOM reference
        setArea(undefined);
      };
    },
    [],
  );

  const contentModifier = classNames(classNameModifier, directionModifier);

  return (
    <div className={getClassNames(styles, 'root', `${classNameModifier}`)}>
      <CTA
        className={getClassNames(styles, 'button', classNames(classNameModifier, directionModifier, { open: isOpen, withIcon: !children }))}
        isOpen={isOpen}
        open={open}
        close={close}
        toggle={toggle}
        tooltipCTARef={ctaRef}
      />
      {isOpen && !insertDangerously && (
        <div
          ref={tipRef}
          className={getClassNames(styles, 'content', classNames(contentModifier, { fixed: direction === 'right' }))}
          style={{
            left: position.x,
            '--arrow': `${arrow.x}px`,
          } as CSSProperties}
        >
          {children}
        </div>
      )}
      {isOpen && insertDangerously && (
        <div
          ref={tipRef}
          className={getClassNames(styles, 'content', classNames(contentModifier, { fixed: direction === 'right' }))}
          style={{
            left: position.x,
            '--arrow': `${arrow.x}px`,
          } as CSSProperties}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: children as string }}
        />
      )}
    </div>
  );
};

export default Tooltip;
